import Button from "@/Components/Common/buttons/Buttons";
import ProductDetailsSkeleton from "@/Components/Common/Loading/ProductDetailLoading";
import { LikedHeart, UnlikedHeart } from "@/Components/icons/Heard";
import ProductCardUpdated from "@/Components/Section/Products/ProductCardUpdated";
import { makeRequest } from "@/helpers/apiWrapper";
import { formatCurrency } from "@/helpers/formatCurrency";
import useCartStore, { Product } from "@/Store/cartStore";
import { useUserStore } from "@/Store/UserInfoStore";
import useWishlistStore from "@/Store/wishlistStore";
import { Box, Divider, Rating, Stack, Tooltip } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  MessageSquareIcon,
  Plus,
  Star
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { TickSvg } from "../Dashboard/Seller/svg/Svg";

const ProductDetails = () => {
  const addToCart = useCartStore((state) => state.addToCart);
  const { user, isLoading: userLoading } = useUserStore()
  const [commentPage, setCommentPage] = useState(1);
  const navigation = useNavigate()
  const [added, setAdded] = useState(false);
  const queryClient = useQueryClient();
  const { toggleWishlist, isInWishlist } = useWishlistStore();
  const navigate = useNavigate();
  const { productId } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["Product", productId, user?._id, commentPage],
    queryFn: () =>
      makeRequest({
        endpoint: `/api/product/${productId}${user?._id
          ? `?accountId=${user._id}&page=${commentPage}`
          : `?page=${commentPage}`
          }`,
        method: "GET",
        isFormData: false,
      }),
    enabled: Boolean(productId) && !userLoading,
    retry: 1,
  });

  const { data: recommendationsData } = useQuery({
    queryKey: ["ProductRecommendation", productId],
    queryFn: () =>
      makeRequest({
        endpoint: `/api/product/recommandations/${productId}`,
      }),
    enabled: Boolean(productId),
    retry: 1,
  });

  const store = data?.data?.store;
  const [isFollowing, setIsFollowing] = useState(store?.isFollowing || false);

  const [followersCount, setFollowersCount] = useState(store?.followingCount || 0);
  useEffect(() => {
    if (store) {
      setFollowersCount(store.followingCount);
      setIsFollowing(store.isFollowing);
    }
  }, [store]);
  const product = {
    ...data?.data?.product,
    convertedAmount: data?.data?.convertedAmount,
  } as Product;
  const [localInventory, setLocalInventory] = useState(
    product?.inventoryCount ?? 0,
  );
  const recommendations =
    recommendationsData?.data?.recommendations || ([] as Product[]);

  const { mutate: toggleFollowerApi } = useMutation({

    mutationFn: (storeId: string) =>
      makeRequest({
        endpoint: `/api/store/toggle-followers/${storeId}`,
        method: "PUT",
      }),
    onMutate: () => {
      setIsFollowing((prev) => {
        setFollowersCount((count) => prev ? count - 1 : count + 1);
        return !prev;
      });
    },
    onSuccess: () => queryClient.invalidateQueries(["Product", productId]),
    onError: () => {
      setIsFollowing((prev) => {
        setFollowersCount((count) => prev ? count - 1 : count + 1);
        return !prev;
      });
    },
  });
  const handleToggleFollower = (storeId) => {
    if (!user) {
      navigation("/login");
      return;
    }
    toggleFollowerApi(storeId);
  }

  const { mutateAsync: toggleWishlistApi } = useMutation({
    mutationFn: (productId: string) =>
      makeRequest({
        endpoint: `/api/product/toggle-wishlist/${productId}`,
        method: "PUT",
      }),
    onMutate: async (productId: string) => {
      toggleWishlist({ _id: productId } as Product);

      return { productId };
    },

    onError: (_err, productId) => {
      toggleWishlist({ _id: productId } as Product);
    },
  });
  const handleToggleWishlist = (product: Product) => {
    if (!user) {
      navigate("/login")
      return
    }
    toggleWishlistApi(product._id);
  };

  const images = useMemo(
    () => product?.imageUrls?.map((img) => img.url).filter(Boolean) || [],
    [product],
  );

  const [mainImage, setMainImage] = useState<string | undefined>(images?.[0]);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isVideoFile = (url?: string) => {
    if (!url) return false;
    return /\.(mp4|mov|webm|ogg|ogv)$/i.test(url);
  };
  useEffect(() => {
    const video = videoRef.current;
    if (video && isVideoFile(mainImage)) {
      video.currentTime = 0;
      video.play().catch(() => { });
    }
  }, [mainImage]);
  useEffect(() => {
    if (product?.inventoryCount !== undefined) {
      setLocalInventory(product.inventoryCount);
    }
  }, [product?.inventoryCount]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productId]);
  const isInventoryZero =
    product?.inventoryCount !== null &&
    product?.inventoryCount !== undefined &&
    product?.inventoryCount === 0;
  useEffect(() => {
    if (!mainImage && images.length > 0) {
      setMainImage(images[0]);
    }
  }, [images, mainImage]);

  const { t } = useTranslation();

  if (isLoading) {
    return <ProductDetailsSkeleton />;
  }
  const handleAddedClick = () => {
    addToCart(product);
    setLocalInventory((prev) => prev - 1);
    setAdded(true);
    setTimeout(() => {
      setAdded(false);
    }, 1000);
  };

  const averageRating = product?.rating?.average || 0;
  const totalReviews = product?.rating?.totalReviews || 0;
  const comments = product?.rating?.comments || [];
  console.log(product);
  if (isError || !product) {
    return (
      <div className="w-full h-full flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-xl font-[600]">Product not found</p>
          <button
            className="mt-4 text-[#000000] text-[18px] px-4 py-2 font-[600] shadow bg-[#FFFFFF] rounded-md border cursor-pointer"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full  h-full flex items-center justify-center relative flex-col">
      <div className="w-[95%] h-full pt-10">
        <div className="w-[100%] flex flex-col lg:flex-row items-start gap-10">
          <div className="w-full lg:flex-2 flex justify-center flex-col gap-2 mb-2">
            <span
              className="flex items-start justify-start gap-2 cursor-pointer mb-4 font-[600]"
              onClick={() => navigate(-1)}
            >
              <ChevronLeft /> {t("Back")}
            </span>
            <div className="flex-1 flex justify-center flex-col gap-2">
              {isVideoFile(mainImage) ? (
                <video
                  src={mainImage}
                  key={mainImage}
                  ref={videoRef}
                  className="w-[56.67vw] h-[69.33vh] object-cover shadow "
                  controls={false}
                  autoPlay
                  muted
                  loop
                />
              ) : (
                <div className="relative ">
                  {/* <span
                    className="absolute top-2 left-2 inline-flex h-7 px-3 justify-center text-white items-center gap-4 rounded-full bg-[#D54033]
"
                  >
                    5% OFF
                  </span> */}
                  {/* <span
                    className="absolute bottom-2 left-2 inline-flex h-7 px-3 justify-center text-black  items-center gap-4 rounded-full bg-[#fff]
"
                  >
                    Silver color{" "}
                  </span> */}
                  <img
                    src={mainImage}
                    alt={product?.name}
                    className="w-[100%] h-[450px] object-cover shadow  "
                  />
                  <div className="flex flex-col gap-3 absolute right-2 top-2">
                    <div
                      className="cursor-pointer flex w-12 h-12 justify-center items-center gap-4 aspect-square rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.10)]"
                      onClick={() => handleToggleWishlist(product)}
                    >
                      {isInWishlist(product?._id) ? <LikedHeart /> : <UnlikedHeart />}
                    </div>
                    {/* <div
                      className=" flex w-12 h-12 justify-center items-center gap-4 aspect-square rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.10)]
"
                    >
                      <LogInIcon />
                    </div>
                    <div
                      className=" flex w-12 h-12 justify-center items-center gap-4 aspect-square rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.10)]
"
                    >
                      <Expand />
                    </div>*/}
                  </div>
                </div>
              )}
            </div>

            <div className="w-full flex items-center justify-center gap-2 mt-3">
              {images.map((file, index) => {
                const isVideo =
                  file.endsWith(".mp4") ||
                  file.endsWith(".mov") ||
                  file.endsWith(".webm") ||
                  file.endsWith(".ogg");
                return isVideo ? (
                  <video
                    key={index}
                    src={file}
                    preload="metadata"
                    onMouseEnter={() => setMainImage(file)}
                    onClick={() => setMainImage(file)}
                    className={`w-[10vw] h-[6vw] object-cover  cursor-pointer transition-all duration-200 ${mainImage === file
                      ? "ring-2 ring-[#5926F0]"
                      : "hover:opacity-80"
                      }`}
                    muted
                    loop
                  />
                ) : (
                  <img
                    key={index}
                    src={file}
                    alt="thumbnail"
                    onClick={() => setMainImage(file)}
                    onMouseEnter={() => setMainImage(file)}
                    className={`lg:w-[8vw] lg:h-[8vw] w-[88px] h-[88px] object-cover  cursor-pointer transition-all duration-200 ${mainImage === file
                      ? "ring-2 ring-[#5926F0]"
                      : "hover:opacity-80"
                      }`}
                  />
                );
              })}
            </div>
          </div>

          <div className="h-full w-full  lg:flex-1 flex flex-col py-0 lg:py-[5vh] gap-[3vh] justify-between">
            <div className="flex items-start gap-1.5 flex-col">
              <div
                className="flex py-1.5 px-4 items-center rounded-full bg-[#F3F3F4] text-[#6D6C79] font-inter text-sm font-medium leading-5 tracking-[-0.14px]
"
              >
                #{product?.categoryId?.name}
              </div>
              <h2 className="text-[22px] font-[600]">{product?.name}</h2>
              <div className="flex items-center gap-4 text-grayText ">
                {product?.shortDescription || t("No description provided.")}
              </div>
              <div className="flex items-center gap-3 font-[600]">
                <span className="text-[#4A32E3]">
                  {" "}
                  {formatCurrency(
                    product?.convertedAmount?.amount || product?.price,
                    product?.convertedAmount?.currency || product?.currency,
                  )}
                </span>
                {/* <span className="text-[#D54033] line-through underline-offset-1">
                  {formatCurrency(
                    product?.convertedAmount?.amount || product?.price,
                    product?.convertedAmount?.currency || product?.currency,
                  )}
                </span> */}
              </div>
              <div className="w-full flex items-center gap-2 text-sm !border-t border-b py-2 border-[#CECED2]">
                <div className="flex items-center gap-[2px]">
                  <div className="flex items-center gap-2">
                    <Rating
                      value={averageRating || 0}
                      precision={0.5}
                      readOnly
                    />

                    <span className="text-sm font-medium">
                      {(averageRating || 0).toFixed(1)} / 5
                    </span>


                  </div>
                </div>{" "}

              </div>


              {/* <div className="w-full flex items-start gap-3 flex-col">
                <p className="text-[16px] font-[600] leading-[24px] tracking-[-0.16px] text-[#0C0A20] font-inter">
                  Hard Drive
                </p>
                <div className="w-full flex items-center gap-2">
                  <span className="w-[100%] text-[#000] font-medium text-[10px] p-[10px] rounded-[3.453px] border-[0.647px] border-[#E4E3E5] ">
                    256 GB ($899.00)
                  </span>
                  <span className="w-[100%] text-[#000] font-medium text-[10px] p-[10px] rounded-[3.453px] border-[0.647px] border-[#E4E3E5] ">
                    256 GB ($899.00)
                  </span>
                </div>
              </div>
              <div className="w-full flex items-start gap-3 flex-col">
                <p className="text-[16px] font-[600] leading-[24px] tracking-[-0.16px] text-[#0C0A20] font-inter">
                  Color Option{" "}
                </p>
                <div className="w-full flex items-center gap-2">
                  {["Midnight", "Silver", "Sky Blue", "Starlight"].map(
                    (color, index) => (
                      <label
                        key={index}
                        className="w-[100%] flex items-center  gap-2 text-[#000] font-medium text-[10px] p-[10px] rounded-[3.453px] border-[0.647px] border-[#E4E3E5]"
                      >
                        <input type="radio" />
                        {color}
                      </label>
                    ),
                  )}
                </div>
              </div> */}
              <div className="flex items-center gap-3 justify-between w-full">
                {product.inventoryCount > 0 && (
                  <p className="text-[14px] font-[600] leading-[24px] tracking-[-0.16px] text-[#0C0A20] font-inter">
                    {product.inventoryCount} {t("items in stock")}
                  </p>
                )}

                {/* <span className="w-auto text-[#000] flex items-center gap-4 font-medium text-[16px]">
                  <Minus className="flex w-[32px] h-[32px] p-[8px] justify-center items-center gap-[8px] rounded-[100px] bg-[#F3F3F4]" />{" "}
                  1{" "}
                  <Plus className="flex w-[32px] h-[32px] p-[8px] justify-center items-center gap-[8px] rounded-[100px] bg-[#F3F3F4]" />
                </span> */}
              </div>
            </div>

            <div className="w-full flex flex-col gap-4">
              <Button
                text="Buy Now"
                variant="primary"
                disabled={isInventoryZero}
                onClick={() => {
                  addToCart(product);
                  navigate("/buy-product");
                }}
              />

              <Button
                onClick={handleAddedClick}
                disabled={isInventoryZero || added}
                text={added ? t("Added") : t("Add to Cart")}
                icon={added ? <TickSvg /> : <Plus size={20} />}
                variant={added ? "success" : "secondary"}
              />
              {isInventoryZero && (
                <p className="text-red-500">
                  {t("This Product is currently Out of stock")}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-start justify-center flex-col lg:flex-row gap-5 w-[100%] font-zalando">
          <div className="h-full w-full lg:flex-2 flex items-start justify-start p-2 flex-col border border-[#E6E6E6] min-h-[300px] ">
            <Box sx={{ width: "100%" }}>
              {/* <Box
                sx={{
                  borderBottom: "1px solid #E6E6E6",
                  width: "100%",
                }}
              >
                <Tabs
                  // value={value}
                  // onChange={handleChange}
                  variant="fullWidth"
                  aria-label="product tabs"
                  sx={{
                    width: "100%",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#666666",
                      padding: "12px 0",
                      borderBottom: "2px solid transparent",
                    },
                    "& .MuiTab-root.Mui-selected": {
                      color: "#000000",
                      fontWeight: 600,
                      borderBottom: "2px solid #000000",
                    },
                    "& .MuiTabs-indicator": {
                      display: "none", // indicator band
                    },
                  }}
                >
                  <Tab label="About" />
                  <Tab label="Specs" />
                  <Tab label="Reviews" />
                  <Tab label="Warnings" />
                  <Tab label="Compare" />
                </Tabs>
              </Box> */}

              <div className="h-auto flex items-center justify-between gap-[10vh] flex-col mt-2 px-4">
                <div className="w-full">
                  <div className="h-auto">
                    <h2 className="text-[32px] font-[700] text-[#000000]">
                      {t("Overview")}
                    </h2>
                  </div>
                  <p className="text-[#101010] font-zalando text-[1rem] font-[500] leading-[1.75rem] tracking-[-0.014rem]">
                    {product?.description || "No description available."}
                  </p>
                </div>
                {/* <div className="relative flex p-[16px_16px_20px_16px] items-start gap-[16px] self-stretch border border-[#DBDBDB] bg-[#F9F9F9] shadow-[0_1px_1.5px_0_rgba(44,54,53,0.03)]">
                  <div className="flex w-[36px] h-[36px]  justify-center items-center gap-[8px] rounded-[100px] bg-[#0C0A20] shadow-[0_1px_1.5px_0_rgba(20,20,20,0.07)]">
                    <Info className="text-black bg-white rounded-full" />
                  </div>
                  <div className="">
                    <p className="overflow-hidden text-ellipsis text-[16px] font-[600] leading-[150%] font-inter text-[var(--Greyscale-900,#0C0A20)]">
                      Product information is provided by our partners
                    </p>
                    <p>
                      We aim for accuracy but cannot guarantee completeness.{" "}
                      <span className="text-blue-500 font-bold underline">
                        See Disclaimer
                      </span>
                    </p>
                  </div>
                  <X className="absolute right-2 top-2" />
                </div> */}
              </div>
            </Box>
          </div>
          <div className="h-full w-full  lg:flex-1 flex items-start justify-start flex-col   ">
            {/* <p className="text-[14px] font-[600] text-[#0C0A20] mb-4">
              Product insights
            </p> */}

            {/* <div className="w-full border border-[#E6E6E6] ">
              <div className="grid grid-cols-2 border-b border-[#E6E6E6]">
                <div className="p-6 border-r border-[#E6E6E6]">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[#0C0A20] text-[14px]">
                      <Zap size={16} className="text-[#0C0A20]" />
                    </span>
                    <p className="text-[14px] font-bold text-[#0C0A20]">
                      Popular choice
                    </p>
                  </div>
                  <p className="text-[24px] font-[600] text-[#0C0A20] mb-1">
                    120+
                  </p>
                  <p className="text-[13px] text-[#6B7280]">
                    Shoppers love this product
                  </p>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[#0C0A20] text-[14px]">
                      <TrendingUp size={16} className="text-[#0C0A20]" />
                    </span>
                    <p className="text-[14px] font-bold text-[#0C0A20]">
                      Trending item
                    </p>
                  </div>
                  <p className="text-[24px] font-[600] text-[#0C0A20] mb-1">
                    25
                  </p>
                  <p className="text-[13px] text-[#6B7280]">
                    Units sold this week.
                  </p>
                </div>
              </div>

              <div className="p-6 border-b border-[#E6E6E6]">
                <div className="flex items-start gap-3">
                  <span className="text-[16px]">
                    <Truck />
                  </span>
                  <div>
                    <p className="text-[14px] font-bold text-[#0C0A20] mb-1">
                      Shipping & Delivery
                    </p>
                    <p className="text-[13px] text-[#6B7280]">
                      Free worldwide delivery · Arrives between Nov 5 – Nov 12
                    </p>
                    <p className="text-[13px] text-[#6B7280] mt-1">
                      Ships within 24 hours after payment confirmation.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 border-b border-[#E6E6E6]">
                <div className="flex items-start gap-3">
                  <span className="text-[16px]">
                    <CreditCard />
                  </span>
                  <div className="w-full">
                    <p className="text-[14px] font-bold text-[#0C0A20] mb-3">
                      Payment options
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="w-[48px] flex items-center justify-center h-[32px] bg-[#F3F3F4] rounded-md" >
                        <PayPalLogo />
                      </div>
                      <div className="w-[48px] flex items-center justify-center h-[32px] bg-[#F3F3F4] rounded-md" >
                        <AppleLogo />
                      </div>
                      
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-start gap-3">
                  <span className="text-[16px]">
                    <Reply />
                  </span>
                  <div>
                    <p className="text-[14px] font-bold text-[#0C0A20] mb-2">
                      Returns & Warranty
                    </p> 
                    <ul className="text-[#555463] font-inter text-[14px] font-medium leading-[30px] tracking-[-0.14px]">
                      <li>30-day return policy.</li>
                      <li>2-year official Apple warranty included.</li>
                      <li>Return label available through your account page.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div> */}

            <p className="text-[14px] font-[600] text-[#0C0A20] mb-4 ">
              {t("About the store")}
            </p>
            <div className="w-full  border border-[#E6E6E6]  p-6">
              <div className="flex items-center gap-4 cursor-pointer">
                <img
                  onClick={() => navigate(`/store/${store?.storeId}`)}
                  src={store?.logo?.url}
                  alt="store"
                  className="w-[56px] h-[56px] rounded-full object-cover"
                />



                <div className="flex flex-col gap-1 flex-1">
                  <div className="flex items-center gap-2">
                    <p
                      onClick={() => navigate(`/store/${store?.storeId}`)}

                      className="text-[16px] font-[600] text-[#0C0A20]">
                      {store?.name}
                    </p>
                    <span className="w-[14px] h-[14px] rounded-full bg-green-500" />
                  </div>

                  <div className="flex items-center gap-1 text-[14px] text-[#6B7280]">
                    {followersCount} {t("Followers")}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 mt-4 text-[14px] text-[#6B7280]">
                {store?.description}
              </div>
              <div className="flex gap-4 mt-6">
                <button
                  className={`flex-1 p-2 cursor-pointer border border-[#E6E6E6]  text-[14px] font-medium
                     text-[#0C0A20] flex items-center justify-center gap-2 ${isFollowing ? "bg-blue-600 text-white" : "bg-transparent text-black"} `}
                  onClick={() => handleToggleFollower(store?._id)}
                >
                  {isFollowing ? t("UnFollow") : <><Plus /> {t("Follow")}</>}
                </button>


                <Tooltip title="Coming Soon" arrow>
                  <button className="flex-1 p-2  border border-[#E6E6E6]  text-[14px] font-medium text-[#0C0A20] flex items-center justify-center gap-2">
                    <MessageSquareIcon /> {t("Chat admin")}
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-start justify-center flex-col lg:flex-row gap-5 w-[100%] font-zalando">
          <div className="h-full w-full lg:flex-2 flex items-start justify-start p-2 flex-col border border-[#E6E6E6] min-h-[300px] ">
            <div className="h-auto px-4">
              <h2 className="text-[32px] font-[700] text-[#000000]">
                {t("Review")}
              </h2>
            </div>
            <div className="flex flex-col lg:flex-row gap-10 px-4 border-b pb-6 w-full">
              <div className="flex items-start flex-col mt-3 gap-2">
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-bold">
                    {averageRating.toFixed(1)}
                  </span>
                  <p className="text-gray-500 text-xl font-bold ">
                    /5
                  </p>
                </div>
                <Rating
                  value={averageRating}
                  precision={0.5}
                  readOnly
                />
                <p className="text-gray-500 mt-2">
                  {totalReviews} {t("Ratings")}
                </p>
              </div>

              <div className="flex flex-col gap-2 lg:w-[300px] w-full">
                {[5, 4, 3, 2, 1].map(star => {
                  const count = product?.rating?.starCounts[star] || 0;
                  const percentage =
                    totalReviews > 0
                      ? (count / totalReviews) * 100
                      : 0;

                  return (
                    <div key={star} className="flex items-center gap-2">
                      <Rating
                        value={star}
                        precision={0.5}
                        readOnly
                      />
                      <div className="flex-1 bg-gray-200 h-2 rounded">
                        <div
                          className="bg-yellow-400 h-2 rounded"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>

                      <span className="w-8 text-sm">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>


            <h3 className="font-black px-3 py-4">Comments ({product?.rating?.comments?.length}) </h3>
            {comments.map((comment) => (
              <div key={comment._id} className="border-b py-4 px-3 w-full">


                <div className="flex items-start gap-2 w-full justify-between">

                  <div className="">
                    <Rating
                      value={comment?.rating}
                      precision={0.5}
                      readOnly
                    />
                    <p className="font-semibold mt-1">
                      {comment?.accountName || "User"}
                    </p>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(comment?.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <p className="text-gray-700 mt-1">
                  {comment?.comment}
                </p>

              </div>
            ))}
            <div className="flex gap-2 mt-2 w-full justify-end items-center">
              <button
                disabled={commentPage <= 1}
                className="flex items-center gap-1 text-sm border p-1 cursor-pointer
                 text-gray-500 disabled:text-gray-300"
                onClick={() => setCommentPage(prev => prev - 1)}
              >
                <ChevronLeft className="p- rounded-full bg-white" />
              </button>
              <span className="text-sm text-gray-500">Page {commentPage}</span>
              <button
                disabled={comments?.length < 10}
                className="flex items-center gap-1 text-sm border p-1 cursor-pointer
                 text-gray-500 disabled:text-gray-300"
                onClick={() => setCommentPage(prev => prev + 1)}
              >
                <ChevronRight className="p- rounded-full bg-white" />
              </button>
            </div>

          </div>

        </div>
        {/* {recommendations.length && (
          <header className="w-[100%] flex items-center justify-between py-4">
            <h2 className="text-[32px] font-[600]">{t("You may also like")}</h2>
          </header>
        )} */}
        <div className="productsGrid">
          {recommendations.map((product: Product, index: number) => (
            <ProductCardUpdated
              key={index}
              img={product?.imageUrls?.[0]?.url || "https://via.placeholder.com/300x200?text=No+Image"}
              title={product?.name}
              price={formatCurrency(
                product?.convertedAmount?.amount || product?.price,
                product?.convertedAmount?.currency || product?.currency,
              )}
              rating={product?.rating || 0}
              sold={product?.sold || 0}
              onclick={() => {
                navigate(`/product/${product?._id}`);
              }}
              liked={isInWishlist(product?._id) || product?.liked || false}
              onToggleWishlist={() => handleToggleWishlist(product)}
              product={product}

            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
